import React, { useEffect, useState } from "react";
import { ShieldCheck, Lock } from "lucide-react";
import ControlLayout from "../components/ControlLayout";
import { api, formatDetail } from "../lib/api";

/**
 * Control Room → Access governance.
 *
 * Read-only catalog of role → default capability mapping. Mutating roles is
 * intentionally not exposed yet — defaults are seeded; per-user overrides
 * happen at the database layer. This page exists so the access boundary is
 * legible.
 */
export default function ControlAccess() {
  const [data, setData] = useState(null);
  const [managers, setManagers] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/access/roles/capabilities"),
      // No /managers endpoint — we just enumerate seeded ones via audit/me.
    ])
      .then(([roles]) => {
        setData(roles.data);
      })
      .catch((e) => setErr(formatDetail(e.response?.data?.detail)));
  }, []);

  return (
    <ControlLayout
      title="Access Governance"
      subtitle="Role → Capability mapping for the People's Engine"
    >
      <div className="grid grid-cols-12 gap-6">
        <section className="col-span-12 lg:col-span-7 border border-neutral-200 bg-white">
          <div className="px-5 py-3 border-b border-neutral-200 flex items-center gap-2">
            <ShieldCheck size={16} />
            <span className="font-mono text-[10px] uppercase tracking-[0.32em]">
              Capability matrix
            </span>
          </div>
          {!data ? (
            <div className="p-8 font-mono text-xs text-neutral-500">Loading…</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                <tr>
                  <th className="text-left px-4 py-2">Role</th>
                  {data.all_capabilities.map((c) => (
                    <th key={c} className="text-center px-2 py-2 whitespace-nowrap">
                      {c.replace("attendance.", "att.")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.roles.map((r) => (
                  <tr key={r.role} className="border-t border-neutral-200" data-testid={`access-row-${r.role}`}>
                    <td className="px-4 py-3 font-mono text-xs uppercase tracking-wider">{r.role}</td>
                    {data.all_capabilities.map((c) => {
                      const has = r.default_capabilities.includes(c);
                      return (
                        <td
                          key={c}
                          className="px-2 py-3 text-center font-mono text-xs"
                          data-testid={`access-cell-${r.role}-${c}`}
                        >
                          {has ? (
                            <span className="text-emerald-700">●</span>
                          ) : (
                            <span className="text-neutral-300">○</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <aside className="col-span-12 lg:col-span-5 space-y-4">
          <div className="border border-neutral-900 bg-[#0F1115] text-neutral-100 p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-amber-300">
              Truth contract
            </div>
            <p className="mt-3 text-sm text-neutral-200 leading-relaxed">
              Presence captures evidence. People&apos;s Engine decides workforce exceptions.
              Control Room authorises access. Payroll consumes only ELIGIBLE finalised records.
            </p>
            <ul className="mt-4 font-mono text-[11px] text-neutral-400 space-y-1">
              <li>• Presence devices have NO approval authority.</li>
              <li>• Manager actions require a fresh People&apos;s Engine session.</li>
              <li>• All transitions are logged immutably in /people/audit.</li>
            </ul>
          </div>

          <div className="border border-neutral-200 bg-white p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-neutral-500 inline-flex items-center gap-2">
              <Lock size={11} /> mutation policy
            </div>
            <p className="mt-3 text-sm text-neutral-700 leading-relaxed">
              Default role mappings are seeded from <code>auth.DEFAULT_ROLE_CAPABILITIES</code>.
              Per-user capability overrides should be applied directly to the
              manager document (governance pattern); UI mutation is deliberately
              out of scope until role-versioning lands.
            </p>
          </div>
        </aside>
      </div>
      {err && <div className="mt-4 text-red-700 font-mono text-sm">{err}</div>}
    </ControlLayout>
  );
}
